import { Descendant } from 'slate'

import { TypographySelectValue } from '@makyoapp/makyo-common'
import { cn, getTypographyTokenStyle, parseSpacing, RawSpacingInput, spacingToStyles } from '@makyoapp/makyo-tw-ui'

import { useCompContext } from '@/context/__project__/CompContext'
import { useParseVariableText } from '@/hooks/__project__/useParseVariableText'
import { COLOR_TYPES } from '@/shared/builder'

import { CompVariableText } from './CompVariableText'

const DEFAULT_SPACING = {
  top: { unit: 'px', value: 0 },
  right: { unit: 'px', value: 0 },
  bottom: { unit: 'px', value: 0 },
  left: { unit: 'px', value: 0 },
}

interface CompHeadlineProps {
  containerClient: any
  detailTableId?: string
  detailRowIndex?: number
  rowId?: string
  headline: Descendant[]
  headlineMargin?: RawSpacingInput
  headlineTypo?: TypographySelectValue
  compTitleAlign?: any
  styles?: React.CSSProperties
  className?: string
  colorType?: string
  backgroundColor?: string
  onClick?: () => void
}

/**
 * CompHeadline
 *
 * @internal - used by other core components
 *
 * @todo - figure out the alignment
 *
 */
export function CompHeadline({
  containerClient,
  detailTableId: __detailTableId, // unused
  detailRowIndex: __detailRowIndex, // unused
  rowId: __rowId, // unused
  headline,
  styles = {},
  className,
  colorType,
  headlineTypo = { typography: 'display medium' },
  headlineMargin = DEFAULT_SPACING,
  compTitleAlign = 'parent',
  backgroundColor,
  onClick,
}: CompHeadlineProps) {
  const { detailTableId, detailRowIndex, rowId: detailRowId } = useCompContext()

  // check if headline empty
  const headlineText = useParseVariableText(headline, containerClient, detailTableId, detailRowIndex, detailRowId)

  if (!headlineText.trim().length) return null

  const isGradient = colorType === COLOR_TYPES.GRADIENT
  const isAdaptive = colorType === COLOR_TYPES.ADAPTIVE

  return headline ? (
    <h3
      className={cn('flex items-center', className)}
      style={{
        ...getTypographyTokenStyle(headlineTypo),
        ...spacingToStyles(parseSpacing(headlineMargin), 'padding'),
        justifyContent: compTitleAlign === 'parent' ? 'center' : compTitleAlign,
        textAlign: compTitleAlign === 'center' ? 'center' : compTitleAlign === 'start' ? 'left' : 'right',
      }}
      onClick={onClick}
    >
      <span
        style={{
          color: isAdaptive ? `var(--color-on-${backgroundColor})` : isGradient ? 'transparent' : 'inherit',
          ...styles,
          WebkitTextFillColor: isGradient ? 'transparent' : 'inherit',
        }}
        className={cn(
          isGradient ? '!bg-clip-text' : !colorType || isAdaptive ? `text-[var(--color-on-${backgroundColor})]` : ''
        )}
      >
        <CompVariableText
          nodes={headline}
          containerClient={containerClient}
          tableId={detailTableId}
          rowIndex={detailRowIndex}
          rowId={detailRowId}
          textProps={{
            style: {
              whiteSpace: 'pre-wrap',
            },
          }}
        />
      </span>
    </h3>
  ) : null
}
